import { useLocation, useMatch } from 'react-router-dom'

import { useNavigation } from 'modules/core/hooks/useNavigation'

import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'
import type { PathSegment } from 'modules/payroll_bundle/lib/types'

import { comparePaths } from 'modules/payroll_bundle/lib/comparePaths'

export const useMatchResultUrl = (policyId?: PathSegment, periodId?: PathSegment) => {
  const { navigation } = useNavigation()
  const { pathname } = useLocation()

  const path = {
    path:
      policyId && periodId
        ? navigation.payrollBundle.policy.period.cycles.results.fullPath({
            policyId,
            periodId,
          })
        : navigation.payrollBundle.results.fullPath(),
    end: false,
  }

  return !!useMatch({ ...path }) || comparePaths(pathname, path.path)
}

export const useMatchPaymentUrl = (policyId?: PathSegment, periodId?: PathSegment) => {
  const { navigation } = useNavigation()
  const { pathname } = useLocation()
  const normalPath =
    policyId && periodId
      ? navigation.payrollBundle.policy.period.cycles.payments.fullPath({
          policyId,
          periodId,
        })
      : ''
  const offcyclePath =
    policyId && periodId
      ? navigation.payrollBundle.policy.period.offcycles.payments.fullPath({
          policyId,
          periodId,
        })
      : ''

  const matchRegularPayments = useMatch({
    path: normalPath,
    end: false,
  })
  const matchOffcyclePayments = useMatch({
    path: offcyclePath,
    end: false,
  })
  return !!matchRegularPayments || matchOffcyclePayments || comparePaths(pathname, normalPath)
}

export const useMatchSupplementsUrl = (policyId?: string | number, periodId?: string | number) => {
  const { navigation } = useNavigation()
  const { pathname } = useLocation()

  const path = {
    path:
      policyId && periodId
        ? navigation.payrollBundle.policy.period.cycles.supplements.fullPath({
            policyId,
            periodId,
          })
        : navigation.payrollBundle.supplements.fullPath(),
    end: false,
  }

  return !!useMatch({ ...path }) || comparePaths(pathname, path.path)
}

export const useMatchIncidencesUrl = () => {
  const { navigation } = useNavigation()
  const { pathname } = useLocation()
  const { hasCompensations } = useHasCompensations()
  const path = hasCompensations
    ? navigation.compensations.employeeUpdates.fullPath()
    : navigation.payrollBundle.incidences.fullPath()

  return !!useMatch({ path, end: false }) || comparePaths(pathname, path)
}

export const useMatchPayrollPolicyCycleUrl = (policyId: PathSegment, periodId: PathSegment) => {
  const { navigation } = useNavigation()
  const { pathname } = useLocation()
  const supplementsPath = navigation.payrollBundle.policy.period.cycles.supplements.fullPath({
    policyId,
    periodId,
  })
  const resultsPath = navigation.payrollBundle.policy.period.cycles.results.fullPath({
    policyId,
    periodId,
  })
  const paymentsPath = navigation.payrollBundle.policy.period.cycles.payments.fullPath({
    policyId,
    periodId,
  })

  const matchSupplementsPath = useMatch({
    path: supplementsPath,
  })
  const matchResultsPath = useMatch({
    path: resultsPath,
  })
  const matchPaymentsPath = useMatch({
    path: paymentsPath,
  })

  return (
    !!matchSupplementsPath ||
    !!matchResultsPath ||
    !!matchPaymentsPath ||
    comparePaths(pathname, supplementsPath) ||
    comparePaths(pathname, resultsPath) ||
    comparePaths(pathname, paymentsPath)
  )
}

export const useMatchOffcycleUrl = (policyId?: PathSegment, periodId?: PathSegment) => {
  const { navigation } = useNavigation()
  const { pathname } = useLocation()

  const resultsPath =
    policyId && periodId
      ? navigation.payrollBundle.policy.period.offcycles.results.fullPath({
          policyId,
          periodId,
        })
      : ''
  const paymentsPath =
    policyId && periodId
      ? navigation.payrollBundle.policy.period.offcycles.payments.fullPath({
          policyId,
          periodId,
        })
      : ''

  const matchResultsPath = useMatch({
    path: resultsPath,
  })
  const matchPaymentsPath = useMatch({
    path: paymentsPath,
  })

  return (
    policyId &&
    periodId &&
    (!!matchResultsPath ||
      !!matchPaymentsPath ||
      comparePaths(pathname, resultsPath) ||
      comparePaths(pathname, paymentsPath))
  )
}

export const useMatchOffcycleResultsUrl = (policyId: PathSegment, periodId: PathSegment) => {
  const { navigation } = useNavigation()
  const { pathname } = useLocation()

  const resultsPath = navigation.payrollBundle.policy.period.offcycles.results.fullPath({
    policyId,
    periodId,
  })

  const matchResultsPath = useMatch({
    path: resultsPath,
  })

  return !!matchResultsPath || comparePaths(pathname, resultsPath)
}

export const useMatchOffcyclePaymentUrl = (policyId: PathSegment, periodId: PathSegment) => {
  const { navigation } = useNavigation()
  const { pathname } = useLocation()

  const paymentsPath = navigation.payrollBundle.policy.period.offcycles.payments.fullPath({
    policyId,
    periodId,
  })

  const matchResultsPath = useMatch({
    path: paymentsPath,
  })

  return !!matchResultsPath || comparePaths(pathname, paymentsPath)
}

export const useMatchCyclePaymentUrl = (policyId: PathSegment, periodId: PathSegment) => {
  const { navigation } = useNavigation()
  const { pathname } = useLocation()

  const paymentsPath = navigation.payrollBundle.policy.period.cycles.payments.fullPath({
    policyId,
    periodId,
  })

  const matchResultsPath = useMatch({
    path: paymentsPath,
  })

  return !!matchResultsPath || comparePaths(pathname, paymentsPath)
}

export const useMatchOffcycleResultImportUrl = (policyId: PathSegment, periodId: PathSegment) => {
  const { navigation } = useNavigation()
  const { pathname } = useLocation()

  const resultsImport = navigation.payrollBundle.policy.period.offcycles.results.import.fullPath({
    policyId,
    periodId,
  })

  const matchResultsPath = useMatch({
    path: resultsImport,
  })

  return !!matchResultsPath || comparePaths(pathname, resultsImport)
}

export const useMatchCycleResultImportUrl = (policyId: PathSegment, periodId: PathSegment) => {
  const { navigation } = useNavigation()
  const { pathname } = useLocation()

  const resultsImport = navigation.payrollBundle.policy.period.cycles.results.import.fullPath({
    policyId,
    periodId,
  })

  const matchResultsPath = useMatch({
    path: resultsImport,
  })

  return !!matchResultsPath || comparePaths(pathname, resultsImport)
}

export const useMatchCycleSupplementsImportUrl = (policyId: PathSegment, periodId: PathSegment) => {
  const { navigation } = useNavigation()
  const { pathname } = useLocation()

  const supplementsImportPath =
    navigation.payrollBundle.policy.period.cycles.supplements.import.fullPath({
      policyId,
      periodId,
    })

  const matchResultsPath = useMatch({
    path: supplementsImportPath,
  })

  return !!matchResultsPath || comparePaths(pathname, supplementsImportPath)
}

export const useMatchCycleResultUrl = (policyId: PathSegment, periodId: PathSegment) => {
  const { navigation } = useNavigation()
  const { pathname } = useLocation()

  const resultsPath = navigation.payrollBundle.policy.period.cycles.results.fullPath({
    policyId,
    periodId,
  })

  const matchResultsPath = useMatch({
    path: resultsPath,
  })

  return !!matchResultsPath || comparePaths(pathname, resultsPath)
}

export const useMatchOffCyclesPaymentUrl = (policyId: PathSegment, periodId: PathSegment) => {
  const { navigation } = useNavigation()
  const { pathname } = useLocation()

  const paymentsPath = navigation.payrollBundle.policy.period.offcycles.payments.fullPath({
    policyId,
    periodId,
  })

  const matchResultsPath = useMatch({
    path: paymentsPath,
  })

  return !!matchResultsPath || comparePaths(pathname, paymentsPath)
}

export const useMatchCyclePaymentsGenerateUrl = (policyId: PathSegment, periodId: PathSegment) => {
  const { navigation } = useNavigation()
  const { pathname } = useLocation()

  const resultsPath = navigation.payrollBundle.policy.period.cycles.payments.generate.fullPath({
    policyId,
    periodId,
  })

  const matchResultsPath = useMatch({
    path: resultsPath,
  })

  return !!matchResultsPath || comparePaths(pathname, resultsPath)
}
